import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { Badge, FieldHelp, Icon, Notice } from '@speaking-coach/ui'
import { AdminApiError } from '../api/client.js'
import { queryKeys } from '../api/query-keys.js'
import { PageHeader } from '../components/page-header'
import { OPERATOR_DETAIL_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { ListPage } from '../common/list-page.js'
import { activeNavigationItem } from '../navigation/admin-navigation.js'
import { adminContentClient } from './api.js'
import type { ContentDashboard } from './types.js'

export function ContentRoute({ children }: {
  children: (dashboard: ContentDashboard) => ReactNode
}): JSX.Element {
  const { pathname } = useLocation()
  const title = activeNavigationItem(pathname)?.label ?? 'Curriculum'
  const query = useQuery({ queryKey: queryKeys.content, queryFn: adminContentClient.get })
  const Shell = ListPage
  if (query.isPending) return <Shell title={title}><p data-testid="content-loading" className="text-body text-text-secondary">Loading curriculum analytics…</p></Shell>
  if (query.error) return <Shell title={title}><Notice variant="danger">
    <p className="font-medium">Could not load curriculum content</p>
    <p>{query.error instanceof AdminApiError ? `${query.error.code}: ${query.error.message}` : query.error.message}</p>
  </Notice></Shell>
  if (!query.data.levels.length && !query.data.programs.length) return <Shell title={title}><div data-testid="content-empty"><Notice variant="neutral">No curriculum content is available.</Notice></div></Shell>
  return <>{children(query.data)}</>
}

const CONTENT_FILTER_PARAMS = new Set([
  'program', 'level', 'theme', 'kind', 'status', 'availability', 'view', 'grade', 'band', 'exception', 'reuse',
])

export function ContentShell({ title, filters, search, filterAction, activeFilterCount, pagination, children }: {
  title: string
  filters?: ReactNode
  search?: ReactNode
  filterAction?: ReactNode
  activeFilterCount?: number
  pagination?: ReactNode
  children: ReactNode
}): JSX.Element {
  const { search: query } = useLocation()
  const countedFilters = Array.from(new URLSearchParams(query).entries())
    .filter(([key, value]) => value && CONTENT_FILTER_PARAMS.has(key)).length
  return <ListPage title={title} activeFilterCount={activeFilterCount ?? countedFilters} pagination={pagination} filters={filters} search={search} filterAction={filterAction}>
    <div className="space-y-4">{children}</div>
  </ListPage>
}

export function ContentKeyBadge({ value }: { value: string }) {
  return <Badge variant="muted" title={`Content key: ${value}`} aria-label={`Content key: ${value}`} className="max-w-64 truncate font-mono">{value}</Badge>
}

export function ContentSourceFile({
  value,
  relationship = 'direct',
}: {
  value: string | null
  relationship?: 'direct' | 'legacy-reference' | 'legacy-fallback' | 'no-active-revision' | 'unknown'
}) {
  const direct = relationship === 'direct'
  const note = relationship === 'legacy-reference'
    ? 'This repository file has the same content key, but it did not produce the displayed legacy snapshot.'
    : relationship === 'legacy-fallback'
      ? 'The current revision cannot be read, so this page shows retained identity fields instead. This repository file did not produce the displayed fallback.'
    : relationship === 'no-active-revision'
      ? 'No revision is currently published, so this file is not the source of the retained fields shown on this page.'
      : relationship === 'unknown'
        ? 'The current revision source could not be determined.'
        : null
  return <dl data-slot="content-source-file" className="sm:col-span-2">
    <dt className="flex items-center gap-1.5 text-text-secondary">
      <Icon name="sourceFile" className="shrink-0" data-slot="content-source-icon" />
      {direct ? 'Source YAML' : 'Repository YAML'}
      <FieldHelp label={direct ? 'source YAML' : 'repository YAML'} size="compact">
        {direct
          ? 'The YAML file under the curriculum content directory that produced the content shown on this page.'
          : 'The YAML file currently associated with this content key. The note below explains why it is not the source of the content shown on this page.'}
      </FieldHelp>
    </dt>
    <dd className={`mt-1 min-w-0 ${direct ? 'text-text-primary' : 'text-text-secondary'}`}>
      {value
        ? <code className="break-words font-mono text-caption">{value}</code>
        : <span className="text-text-secondary">No repository source</span>}
      {note ? <p className="mt-1 text-caption text-text-secondary">{note}</p> : null}
    </dd>
  </dl>
}

export function ContentDetailShell({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return <main className={OPERATOR_PAGE_MAIN}><div className={`${OPERATOR_DETAIL_PAGE} space-y-4`}><PageHeader title={title} action={action} />{children}</div></main>
}
